import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Loader2, Pencil, Plus, Search, Settings2 } from "lucide-react";
import { api, inr, logError, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ListState } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const SEARCH_DEBOUNCE_MS = 250;

export const STATUS_COLOR = {
  Draft: "secondary",
  "Pending Approval": "outline",
  Approved: "default",
  Sent: "default",
  Negotiation: "outline",
  Accepted: "default",
  Rejected: "destructive",
  Expired: "destructive",
  Converted: "default",
};

export default function Quotations() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "60" });
      if (status !== "all") params.set("status", status);
      if (search) params.set("search", search);
      if (minAmount) params.set("min_amount", minAmount);
      const { data } = await api.get(`/quotations?${params}`);
      setItems(data.items);
    } catch (e) {
      logError("quotations", e);
    } finally {
      setLoading(false);
    }
  }, [status, search, minAmount]);

  useEffect(() => {
    const t = setTimeout(load, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div>
      <PageHeader
        testId="quotations-header"
        title="Quotations"
        subtitle="Photographic proposals with revisions, approvals, PDF and WhatsApp delivery."
        actions={
          <>
            <Button asChild variant="outline" size="sm" className="gap-1.5"
              data-testid="template-editor-button">
              <Link to="/quotations/template-editor">
                <Settings2 className="h-4 w-4" /> Template Editor
              </Link>
            </Button>
            {can("quotations", "create") && (
              <Button asChild data-testid="new-quotation-button" className="gap-1.5">
                <Link to="/quotations/new">
                  <Plus className="h-4 w-4" /> New Quotation
                </Link>
              </Button>
            )}
          </>
        }
      />
      <div className="flex flex-wrap gap-3 border-b border-border px-4 py-3 sm:px-6">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="quotations-search-input"
            className="pl-9"
            placeholder="Number, customer, project"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="quotations-status-filter" className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(STATUS_COLOR).map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          data-testid="quotations-min-amount-input"
          className="w-40"
          type="number"
          placeholder="Min value ₹"
          value={minAmount}
          onChange={(e) => setMinAmount(e.target.value)}
        />
      </div>

      <div className="p-4 sm:p-6">
        <ListState loading={loading} isEmpty={items.length === 0}
          emptyTitle="No quotations yet"
          emptyHint="Build your first photographic quotation in a couple of minutes.">
            <div className="surface overflow-x-auto" data-testid="quotations-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Number</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="hidden md:table-cell">Project</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden sm:table-cell">Date</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((q) => (
                    <TableRow key={q.id} data-testid={`quotation-row-${q.id}`}>
                      <TableCell>
                        <Link
                          to={`/quotations/${q.id}`}
                          data-testid={`quotation-link-${q.id}`}
                          className="font-mono-num text-sm font-medium text-primary hover:underline"
                        >
                          {q.number}
                        </Link>
                        {q.revision > 0 && (
                          <Badge variant="outline" className="ml-2 text-[10px]">
                            R{q.revision}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{q.customer_name}</TableCell>
                      <TableCell className="hidden text-sm md:table-cell">
                        {q.project_name || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_COLOR[q.status] || "secondary"} className="text-[10px]">
                          {q.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden text-sm sm:table-cell">{shortDate(q.date)}</TableCell>
                      <TableCell className="text-right font-mono-num text-sm">
                        {inr(q.grand_total)}
                      </TableCell>
                      <TableCell className="text-right">
                        {can("quotations", "edit") && !["Approved", "Converted"].includes(q.status) && (
                          <Button asChild size="sm" variant="outline" className="gap-1">
                            <Link to={`/quotations/${q.id}/edit`} data-testid={`quotation-edit-button-${q.id}`}>
                              <Pencil className="h-3.5 w-3.5" /> Edit
                            </Link>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
        </ListState>
      </div>
    </div>
  );
}
